import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Checkbox } from "@ui/checkbox/Checkbox";
import { Error } from "@ui/error/Error";
import { IconCheck } from "@ui/icons/IconCheck";
import Input from "@ui/input/Input";
import { HLine } from "@ui/lines/HLine";
import Panel from "@ui/panel/Panel";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { CountriesData } from "@ui/utils/CountriesData";
import Signal from "badmfck-signal";
import Decimal from "decimal.js";
import { Fragment, useEffect, useState } from "react";
import { GD } from "src/GD";
import { CardFeeEntry, CardFeeSection, getFees } from "src/tools/Fee";
import { getCardType } from "src/utils/tools";

export const S_TRANSACTION_FEE_POPUP = new Signal<{ cardNumber?: string; merchantMcc?: string; merchantCountry?: string; amount?: string } | null>(
	"S_TRANSACTION_FEE_POPUP",
);

//const keys: (keyof typeof FEE)[] = <(keyof typeof FEE)[]>Object.keys(FEE);
declare interface CardType {
	value: CardFeeSection["card_type"];
	label: string;
}

declare interface RegionType {
	id: "domestic" | "intra" | "inter";
	label: string;
}

const cardTypes: CardType[] = [
	{
		value: "mastercard",
		label: "Mastercard",
	},
	{
		value: "visa",
		label: "Visa",
	},
	{
		value: "visa_debit",
		get label() { return msg("visaDebit"); },
	},
];

// const regionTypes: RegionType[] = [
// 	{
// 		id: "domestic",
// 		label: "Domestic",
// 	},
// 	{
// 		id: "intra",
// 		label: "Intra",
// 	},
// 	{
// 		id: "inter",
// 		label: "Inter",
// 	},
// ];

export const TransactionFeePopup = () => {
	useLocale();
	const requestData = S_TRANSACTION_FEE_POPUP.use();
	const [error, setError] = useState<IError | undefined>(undefined);
	// const [regionType, setRegionType] = useState<RegionType>(regionTypes[0]);
	const [amount, setAmount] = useState<{ value: string }>({ value: requestData?.amount || "0" });
	const [merchantCountry, setMerchantCountry] = useState<{ value: string }>({ value: requestData?.merchantCountry || "" });
	const [cardCountry, setCardCountry] = useState<{ value: string }>({ value: "" });
	const [total, setTotal] = useState<number>(0);
	const [cardType, setCardType] = useState<{ value: CardFeeSection["card_type"] }>({ value: "mastercard" });
	const [transactionType, setTransactionType] = useState<{ value: "consumer" | "commercial" }>({ value: "consumer" });
	const [time, setTime] = useState<{ value: string }>({ value: Date.now().toString() });
	const [card, setCard] = useState<{ value: string }>({ value: requestData?.cardNumber || "" });
	const [merchantMcc, setMerchantMcc] = useState<{ value: string }>({ value: requestData?.merchantMcc || "" });
	const [cardNotPresent, setCardNotPresent] = useState<{ value: boolean }>({ value: false });
	const [status, setStatus] = useState<{ value: "approved" | "declined" }>({ value: "approved" });
	const [three3ds2_or_mdes, setThree3ds2_or_mdes] = useState<{ value: boolean }>({ value: false });
	const [mdesAuthenticatedTransactions, setMdesAuthenticatedTransactions] = useState<{ value: boolean }>({ value: false });
	const [nonSecureEcomOrMotoTrxWithCvc2, setNonSecureEcomOrMotoTrxWithCvc2] = useState<{ value: boolean }>({ value: false });
	const [cnpWithMcTokens, setCnpWithMcTokens] = useState<{ value: boolean }>({ value: false });
	const [nonSecureCnpOnly, setNonSecureCnpOnly] = useState<{ value: boolean }>({ value: false });
	const [cnpOnly, setCnpOnly] = useState<{ value: boolean }>({ value: false });
	const [f2fAndCnp, setF2fAndCnp] = useState<{ value: boolean }>({ value: false });
	const [cnpNeitherSecureNorTokenized, setCnpNeitherSecureNorTokenized] = useState<{ value: boolean }>({ value: false });
	const [cardholderInitiatedWithDataQuality, setCardholderInitiatedWithDataQuality] = useState<{ value: boolean }>({ value: false });
	const [cnpWithVisaToken, setCnpWithVisaToken] = useState<{ value: boolean }>({ value: false });
	const [cryptoPurchaseTransaction, setCryptoPurchaseTransaction] = useState<{ value: boolean }>({ value: false });
	const [cvv2VerificationWithMatchOrNoMatchResult, setCvv2VerificationWithMatchOrNoMatchResult] = useState<{ value: boolean }>({ value: false });
	const [fees, setFees] = useState<
		| (
				| ({
						fee: number;
						name: string;
						description?: string;
						rule?: CardFeeEntry;
				  } & { selected: boolean })
				| undefined
		  )[]
		| undefined
	>(undefined);

	useEffect(() => {
		if (requestData) {
			setAmount({ value: requestData.amount || "0" });
			setCard({ value: requestData.cardNumber || "" });
			setMerchantMcc({ value: requestData.merchantMcc || "" });
			setMerchantCountry({ value: requestData.merchantCountry || "" });
		}
	}, [requestData]);

	if (!requestData) return null;

	const closePopup = () => {
		setAmount({ value: "0" });
		setCard({ value: "" });
		setMerchantMcc({ value: "" });
		setMerchantCountry({ value: "" });
		setFees(undefined);
		setTotal(0);
		setError(undefined);
		S_TRANSACTION_FEE_POPUP.invoke(null);
	};

	const recalculateSum = (
		fs:
			| (
					| ({
							fee: number;
							name: string;
							description?: string;
							rule?: CardFeeEntry;
					  } & { selected: boolean })
					| undefined
			  )[]
			| undefined,
	) => {
		if (fs) {
			let total = new Decimal(0);
			fs.forEach(fee => {
				if (fee && fee.selected) {
					total = total.plus(fee.fee);
				}
			});
			setTotal(total.toNumber());
		}
	};

	const requestCardInfo = async (cardNumber: string) => {
		if (cardNumber.length > 15) {
			const cardReq = await GD.REQ_API.request({
				method: "card/info",
				data: {
					pan: cardNumber,
				},
			});

			const detectedType = getCardType(cardNumber);
			if (cardReq.data) {
				//!TODO: types;
				const cardInfo: {
					bin_hash: string;
					bin_digits: string;
					pan_length: string;
					bin: string;
					bank: string;
					card: string;
					type: string;
					level: string;
					country: string;
					country_code: string;
					date_updated: string;
					checker: string;
				} = cardReq.data;

				let cardType: CardType | null = null;
				if (detectedType !== "unknown") {
					if (detectedType === "mastercard") {
						cardType = cardTypes[0];
					} else if (detectedType === "visa") {
						cardType = cardTypes[1];
					}
				}
				if (cardType) {
					setCardType({ value: cardType.value });
				}
				if (cardInfo.country_code) {
					setCardCountry({ value: cardInfo.country_code });
				}
			} else {
				setError(cardReq.error);
			}
		}
	};

	const convertCurrency = (amount: Decimal, fromCurrency: string, toCurrency?: string): Decimal => {
		if (fromCurrency === toCurrency) {
			return amount;
		}
		//const rate = GD.EXCHANGE_RATES.getRate(fromCurrency, toCurrency);
		const rate = new Decimal(1.18);
		return amount.mul(rate);
	};

	const getFeesArray = async () => {
		setFees(undefined);
		setTotal(0);

		const fees = getFees(
			convertCurrency,
			{ cardType: cardType.value, cardRegion: cardCountry.value },
			{ country: merchantCountry.value, mcc: merchantMcc.value },
			parseFloat(amount.value),
			parseFloat(time.value),
			transactionType.value,
			cardNotPresent.value,
			status.value,
			three3ds2_or_mdes.value,
			mdesAuthenticatedTransactions.value,
			nonSecureEcomOrMotoTrxWithCvc2.value,
			cnpWithMcTokens.value,
			nonSecureCnpOnly.value,
			cnpOnly.value,
			f2fAndCnp.value,
			cnpNeitherSecureNorTokenized.value,
			cardholderInitiatedWithDataQuality.value,
			cnpWithVisaToken.value,
			cryptoPurchaseTransaction.value,
			cvv2VerificationWithMatchOrNoMatchResult.value,
		);
		const newFees = fees.map(fee => {
			if (fee) return { ...fee, selected: true };
		});
		setFees(newFees);
		recalculateSum(newFees);
		setError(undefined);
	};

	return (
		<Panel
			sx={{
				minWidth: "600px",
				maxWidth: "900px",
				height: "85vh",
				padding: "0",
			}}
			decorator="absolute"
			label={msg("feeInterchange")}
			onClose={closePopup}>
			<Box sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
				<VBox gap="12px" sx={{ flex: 1, overflow: "hidden" }}>
					<HBox gap="12px" sx={{ alignItems: "end", flexWrap: "wrap" }}>
						<Input
							label={msg("card")}
							onChange={v => {
								setCard({ value: v });
							}}
							data={card}
							onBloorProps={() => requestCardInfo(card.value)}
							name="value"
							size="small"
						/>
						<Input
							label={msg("amount")}
							onChange={v => {
								setAmount({ value: v });
							}}
							data={amount}
							name="value"
							units="EUR"
							size="small"
						/>

						<Input
							label={msg("transactionTimestamp")}
							onChange={v => {
								setTime({ value: v });
							}}
							data={time}
							name="value"
							units="EUR"
							size="small"
						/>

						<Input
							label={msg("merchantMcc")}
							onChange={v => {
								setMerchantMcc({ value: v });
							}}
							data={merchantMcc}
							name="value"
							size="small"
						/>
						<Select
							sx={{ width: "250px" }}
							label={msg("merchantCountry")}
							size="small"
							name={"value"}
							onChange={val => {
								setMerchantCountry({ value: val.alpha2 });
							}}
							data={merchantCountry}
							values={CountriesData.countries}
						/>
						<Select
							sx={{ width: "250px" }}
							label={msg("cardType")}
							size="small"
							name={"value"}
							onChange={val => {
								setCardType({ value: val.value as CardFeeSection["card_type"] });
							}}
							data={cardType}
							values={[
								{ value: "mastercard", label: "Mastercard" },
								{ value: "visa", label: "Visa" },
								{ value: "visa_debit", label: msg("visaDebit") },
							]}
						/>
						<Select
							sx={{ width: "250px" }}
							label={msg("transactionType")}
							size="small"
							name={"value"}
							onChange={val => {
								setTransactionType({ value: val.value as "consumer" | "commercial" });
							}}
							data={transactionType}
							values={[
								{ value: "consumer", label: msg("consumer") },
								{ value: "commercial", label: msg("commercial") },
							]}
						/>
						<Select
							sx={{ width: "250px" }}
							label={msg("transactionStatus")}
							size="small"
							name={"value"}
							onChange={val => {
								setStatus({ value: val.value as "approved" | "declined" });
							}}
							data={status}
							values={[
								{ value: "approved", label: msg("approved") },
								{ value: "declined", label: msg("declined") },
							]}
						/>
						<Select
							sx={{ width: "250px" }}
							label={msg("cardCountry")}
							size="small"
							name={"value"}
							onChange={val => {
								setCardCountry({ value: val.alpha2 });
							}}
							data={cardCountry}
							values={CountriesData.countries}
						/>
						<Checkbox
							label={msg("cardNotPresent")}
							data={cardNotPresent}
							name="value"
							onChange={val => {
								setCardNotPresent({ value: val });
							}}
						/>

						<Checkbox
							label="EMV 3DS (3DS2)"
							data={three3ds2_or_mdes}
							name="value"
							onChange={val => {
								setThree3ds2_or_mdes({ value: val });
							}}
						/>
						<Checkbox
							label={msg("mdesAuthenticatedTransactionsAllToken")}
							data={mdesAuthenticatedTransactions}
							name="value"
							onChange={val => {
								setMdesAuthenticatedTransactions({ value: val });
							}}
						/>
						<Checkbox
							label={msg("nonSecureEComOrMotoTrxWithCvc2")}
							data={nonSecureEcomOrMotoTrxWithCvc2}
							name="value"
							onChange={val => {
								setNonSecureEcomOrMotoTrxWithCvc2({ value: val });
							}}
						/>

						<Checkbox
							label={msg("cnpTransactionThatAreProcessedWithMcTokens")}
							data={cnpWithMcTokens}
							name="value"
							onChange={val => {
								setCnpWithMcTokens({ value: val });
							}}
						/>
						<Checkbox
							label={msg("nonSecureCnpTrxOnly")}
							data={nonSecureCnpOnly}
							name="value"
							onChange={val => {
								setNonSecureCnpOnly({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cnpTrxOnly")}
							data={cnpOnly}
							name="value"
							onChange={val => {
								setCnpOnly({ value: val });
							}}
						/>
						<Checkbox
							label={msg("f2fAndCnpTransaction")}
							data={f2fAndCnp}
							name="value"
							onChange={val => {
								setF2fAndCnp({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cnpTransactionsThatAreNeitherSecureNorTokenized")}
							data={cnpNeitherSecureNorTokenized}
							name="value"
							onChange={val => {
								setCnpNeitherSecureNorTokenized({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cardholderInitiatedTransactionThatMeetTheDataQualityMandate")}
							data={cardholderInitiatedWithDataQuality}
							name="value"
							onChange={val => {
								setCardholderInitiatedWithDataQuality({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cnpTransactionProcessedWithAVisaToken")}
							data={cnpWithVisaToken}
							name="value"
							onChange={val => {
								setCnpWithVisaToken({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cryptoPurchaseTransaction")}
							data={cryptoPurchaseTransaction}
							name="value"
							onChange={val => {
								setCryptoPurchaseTransaction({ value: val });
							}}
						/>
						<Checkbox
							label={msg("cvv2VerificationWithMatchOrNoMatchResult")}
							data={cvv2VerificationWithMatchOrNoMatchResult}
							name="value"
							onChange={val => {
								setCvv2VerificationWithMatchOrNoMatchResult({ value: val });
							}}
						/>

						<Button
							variant="primarySmall"
							size="small"
							title={msg("calculateFee")}
							onClick={() => {
								getFeesArray();
							}}
						/>
					</HBox>

					{error && <Error error={error} />}

					<VBox sx={{ flex: 1, overflow: "auto" }}>
						{fees &&
							fees.map((fee, i) => {
								if (fee === undefined || fee.rule === undefined) {
									return null;
								}
								return (
									<Fragment key={i}>
										<HBox
											onClick={() => {
												fee && (fee.selected = !fee?.selected);
												setFees([...fees]);
												recalculateSum(fees);
											}}
											gap="14px"
											sx={{
												backgroundColor: fee && !fee.selected ? "unset" : "#d4f0ff",
												cursor: "pointer",
												minHeight: "50px",
												flexShrink: "0",
												alignItems: "center",
												paddingTop: "8px",
												paddingBottom: "8px",
												paddingLeft: "12px",
												paddingRight: "12px",
											}}
											key={i}>
											{fee === undefined || fee.rule === undefined ? (
												<Typo color="red">{msg("feeEntryNotFound")}</Typo>
											) : (
												<>
													<Box
														sx={{
															width: "21px",
															height: "21px",
															display: "flex",
															flexShrink: "0",
															alignItems: "center",
															justifyContent: "center",
														}}>
														{fee.selected && <IconCheck />}
													</Box>
													<VBox sx={{ maxWidth: "700px" }}>
														<Typo>{fee.name}</Typo>
														<Typo sx={{ fontSize: "12px" }} color="#646464">
															{fee.description ?? ""}
														</Typo>
													</VBox>

													{fee.rule === undefined ? (
														<Typo color="red" sx={{ marginLeft: "auto", textAlign: "right" }}>{msg("notFound")}</Typo>
													) : (
														<Typo sx={{ width: "100px", marginLeft: "auto", textAlign: "right" }}>
															{/* //!TODO: currency; */}
															{fee.fee} {"EUR"}
														</Typo>
													)}
												</>
											)}
										</HBox>
										{i < fees.length - 1 && <HLine color="#c5c5c5" key={"line" + i} />}
									</Fragment>
								);
							})}
					</VBox>

					{fees && (
						<HBox gap="16px" sx={{ alignItems: "center", paddingTop: "16px", paddingBottom: "12px" }}>
							<Typo bold>{msg("totalFee")}</Typo>
							<Typo sx={{ marginLeft: "auto" }}>{total} {"EUR"}</Typo>
						</HBox>
					)}
				</VBox>

				<Spacer size="20px" />
				<VBox sx={{ gap: "12px", width: "100%" }}>
					<HBox gap="12px" sx={{ width: "100%" }}>
						<Button
							sx={{ flex: 1 }}
							title={msg("close")}
							onClick={() => {
								closePopup();
							}}
						/>
					</HBox>
				</VBox>
			</Box>
		</Panel>
	);
};
